import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [unverified, setUnverified] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setUnverified(false);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      const msg = errMsg(err);
      if (err?.response?.status === 403 && msg.toLowerCase().includes("verify")) {
        setUnverified(true);
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      const res = await api.post("/auth/resend-verification", { email });
      toast.success(res.data.message);
      navigate("/check-email", { state: { email, devVerifyUrl: res.data.dev_verify_url } });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <AuthLayout>
      <Card className="border shadow-none">
        <CardHeader>
          <CardTitle className="text-xl">Welcome back</CardTitle>
          <CardDescription>Log in with your work email.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" autoComplete="current-password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Your password"
                data-testid="login-password-input" />
            </div>
            {unverified && (
              <div className="rounded-lg bg-[hsl(var(--status-due-soon)/0.10)] px-3 py-2.5 text-sm text-[hsl(var(--status-due-soon))]">
                Your email isn't verified yet.{" "}
                <button type="button" onClick={resend} className="underline font-medium" data-testid="login-resend-verification-button">
                  Resend the link
                </button>
              </div>
            )}
            <Button type="submit" className="w-full rounded-xl" disabled={busy} data-testid="login-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            New to BIIPP?{" "}
            <Link to="/signup" className="font-medium text-foreground underline underline-offset-4" data-testid="login-signup-link">
              Create your organization
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
